import {Button, Tooltip} from '@vector';
import {QuestionCircleBoldIcon} from '@solar-icons/react';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import {HOME_URL, REGISTER_URL} from '../routes';
import SignIn from '../sections/SignIn';
import styles from './Layout.module.css';
import themes from './brandThemes.module.css';

/*
 * The Cambridge text logo (no shield) and the HOTmaths logo as two separate marks, replacing the combined
 * image (home/template/v2/img/logos/hotmaths-logo.svg) that put the Cambridge wordmark in an orange box.
 * Following the Cambridge brand guidelines (brand.cambridge.org/guide/logo): black on a light background
 * with nothing behind it, and at least 150px wide on screen.
 *
 * The guidelines keep the Cambridge logo out of website footers, so the footer carries the HOTmaths logo
 * and the copyright line only.
 */
const CAMBRIDGE_LOGO_URL = '/assets/branding/checkpoints/cambridge-logo-text.svg';
const HOTMATHS_LOGO_URL = '/users/generalFiles/images/logos/hotmaths.svg';

/**
 * Page chrome for the redesigned homepage: skip link, sticky translucent header, `<main>` slot,
 * footer.
 *
 * A dedicated fork of `SiteShell.js`, not an edit of it - `SiteShell.js` is shared with `Home.js`
 * (which must stay unaffected) and with the registration and password-reset pages, which this
 * redesign is not touching. New classes throughout, so nothing here can leak onto those pages.
 *
 * Reuses `SignIn` unchanged: its trigger is already a bare vector `Button` with no styling
 * dependency on the old page chrome, and the design handoff doesn't redesign the dialog itself.
 */
/**
 * The brand's own logo (`content.logo`) in place of HOTmaths', e.g. Cambridge LANTITE Edge. One that already carries
 * the Cambridge name (`includesCambridge`) stands alone in the header. A white logo made for dark backgrounds
 * (`onDark`) is drawn black on the light header, and left as it is on the dark footer.
 */
function BrandLogo({logo, className, onLight = false}) {
    const drawBlack = onLight && logo.onDark;
    return (
        <img className={`${className} ${drawBlack ? styles.logoOnDark : ''}`} src={logo.src} alt={logo.alt}/>
    );
}

export default function Layout({config, nav, helpHref, productName, homeHref = HOME_URL,
                                registerHref = REGISTER_URL, footerLinks, logo, theme, showSignIn = true,
                                showJoin = true, showBack = false, copyright = 'Cambridge University Press',
                                headerAction, children}) {
    const currentPath = typeof window === 'undefined' ? '' : window.location.pathname;

    return (
        <div className={`${styles.page} ${themes[theme] || ''} ch-site`}>
            <a className={styles.skipLink} href="#home-main">Skip to main content</a>

            <header className={styles.header}>
                <a className={styles.logoLockup} href={homeHref} aria-label={`${productName} home`}>
                    <span className={styles.logos}>
                        {!logo?.includesCambridge && (
                            <img className={styles.cambridgeLogo} src={CAMBRIDGE_LOGO_URL}
                                 alt="Cambridge"/>
                        )}
                        {logo ? (
                            <BrandLogo logo={logo} className={styles.brandLogo} onLight/>
                        ) : (
                            <img className={styles.hotmathsLogo} src={HOTMATHS_LOGO_URL} alt="HOTmaths"/>
                        )}
                    </span>
                </a>

                {nav?.length > 0 && (
                    <nav className={styles.nav} aria-label="Main">
                        <ul>
                            {nav.map((item) => (
                                <li key={item.label}>
                                    <a href={item.href}
                                       aria-current={item.href === currentPath ? 'page' : undefined}>
                                        {item.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>
                )}

                <div className={styles.actions}>
                    {showBack && (
                        <Button href={homeHref} icon={<ArrowBackRounded fontSize="small"/>}>
                            Back to home
                        </Button>
                    )}
                    {helpHref && (
                        <Tooltip title="Help (opens in a new tab)">
                            {/* A new tab, as the old site's help window was, so the page stays where it was. */}
                            {/* ch-home-help: a brand theme with a dark header recolours it (brandThemes.module.css). */}
                            <Button type="text" href={helpHref} target="_blank" rel="noopener noreferrer"
                                    className="ch-home-help"
                                    aria-label="Help (opens in a new tab)"
                                    icon={<QuestionCircleBoldIcon size={24}/>}/>
                        </Tooltip>
                    )}
                    {showSignIn && <SignIn useTwoStepLogin={config?.useTwoStepLogin ?? false}/>}
                    {showJoin && <Button surface="accent" href={registerHref}>Join now</Button>}
                    {/* A page's own main action, e.g. Edjin's "Contact us", which has no sign-in or join. */}
                    {headerAction}
                </div>
            </header>

            <main id="home-main">{children}</main>

            <footer className={styles.footer}>
                <div className={styles.footerInner}>
                    <div className={styles.footerLogos}>
                        {logo ? (
                            <BrandLogo logo={logo} className={styles.brandLogoFooter}/>
                        ) : (
                            <img className={styles.hotmathsLogo} src={HOTMATHS_LOGO_URL} alt={productName}/>
                        )}
                    </div>
                    {footerLinks?.length > 0 && (
                        <nav aria-label="Legal">
                            <ul className={styles.footerLinks}>
                                {footerLinks.map((link) => (
                                    <li key={link.label}><a href={link.href}>{link.label}</a></li>
                                ))}
                            </ul>
                        </nav>
                    )}
                    <p>&copy; {new Date().getFullYear()} {copyright}</p>
                </div>
            </footer>
        </div>
    );
}
